import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { allGenres, allWorks } from "@/utils/catalog";

const values = [
  {
    icon: "ri-book-open-line",
    title: "清晰的剧情梳理",
    text: "把复杂的长篇剧集拆解成可读的剧情脉络，关键转折、人物关系一目了然。",
  },
  {
    icon: "ri-user-heart-line",
    title: "有温度的角色分析",
    text: "不只讲“发生了什么”，更关注人物为什么这样选择，帮你看懂角色的动机。",
  },
  {
    icon: "ri-compass-3-line",
    title: "靠谱的观影推荐",
    text: "基于题材标签与风格相似度推荐作品，减少在海量片库里盲目翻找的时间。",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 妻子的朋友影视剧情门户",
    description:
      "了解妻子的朋友的网站定位、内容范围与联系方式。我们专注影视剧情介绍、角色分析与观影推荐。",
    keywords: "关于妻子的朋友,影视剧情门户,联系方式,版权声明,影视推荐",
  });

  return (
    <div className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <header className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-primary-900 p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-accent-300 uppercase">
          <span className="h-3 w-1 rounded-full bg-accent-500"></span>
          关于妻子的朋友
        </span>
        <h1 className="mt-2 font-heading text-3xl font-bold text-background-50 md:text-4xl">
          认真看懂每一部影视剧
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-primary-100 md:text-base">
          妻子的朋友是一个专注于影视剧情介绍与观影推荐的内容门户。
          我们把电影、电视剧与网络剧的剧情脉络、角色分析与看点整理成清晰易读的内容，
          帮助热爱影视的你，在有限的时间里找到真正值得看的作品。
        </p>
        <div className="mt-6 flex flex-wrap gap-6">
          <div>
            <div className="font-heading text-2xl font-black text-background-50">{allWorks.length}+</div>
            <div className="text-xs text-primary-200">收录影视作品</div>
          </div>
          <div>
            <div className="font-heading text-2xl font-black text-background-50">{allGenres.length}+</div>
            <div className="text-xs text-primary-200">题材标签</div>
          </div>
          <div>
            <div className="font-heading text-2xl font-black text-background-50">3</div>
            <div className="text-xs text-primary-200">内容分类</div>
          </div>
        </div>
      </header>

      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-10">
          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              网站定位
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们服务于想快速获取影视剧情详情与角色分析的观众群体。
              无论是追更时间紧张的上班族，还是想提前了解剧情再决定是否收看的学生党，
              都能在这里用更少的阅读成本，掌握一部作品的核心信息。
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              内容范围
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              内容覆盖<strong className="font-medium text-foreground-900">电影、电视剧、网络剧</strong>
              三大分类，包含剧情梗概、角色分析、作品元信息与相似作品推荐。
              题材涵盖悬疑、犯罪、科幻、爱情、古装、家庭、谍战等方向，
              并持续通过“专题合集”的方式，成体系地推荐同一主题下的作品。
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 text-sm font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{v.text}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              使用及版权声明
            </h2>
            <ul className="mt-3 flex flex-col gap-2.5 text-sm leading-7 text-foreground-700">
              <li className="flex gap-2">
                <span className="mt-2 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                本站所有影视作品、评分、演员等信息均为演示示例数据，不代表真实作品信息。
              </li>
              <li className="flex gap-2">
                <span className="mt-2 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                本站未接入任何真实影视数据库、在线播放或下载接口，不提供任何观影资源。
              </li>
              <li className="flex gap-2">
                <span className="mt-2 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                页面内容仅供学习与交流参考，请勿用于商业用途。
              </li>
            </ul>
          </section>
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">联系我们</h2>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              内容纠错、合作洽谈或意见建议，欢迎通过邮件与我们联系。
            </p>
            <a
              href="mailto:contact@wife-friend.example.com"
              className="mt-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-mail-line text-xl"></i>
              </span>
              <span className="min-w-0">
                <span className="block text-xs text-foreground-500">邮箱</span>
                <span className="block truncate text-sm font-medium text-foreground-900">
                  contact@wife-friend.example.com
                </span>
              </span>
            </a>
            <p className="mt-3 text-[11px] leading-relaxed text-foreground-400">
              我们通常在 2 个工作日内回复。示例邮箱仅用于演示，不会真实接收邮件。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">快速开始</h2>
            <div className="mt-3 flex flex-col gap-2">
              <Link
                to="/category/movie"
                className="flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-film-line"></i>
                </span>
                浏览电影
              </Link>
              <Link
                to="/special"
                className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-bookmark-3-line"></i>
                </span>
                查看专题合集
              </Link>
              <Link
                to="/search"
                className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-700 transition-colors hover:bg-accent-200 cursor-pointer"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-search-line"></i>
                </span>
                搜索影视作品
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}